import React, { useState, useCallback } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/app/store'
import { parseDateToDayjs } from '@/utils/parseDateToDayjs'

export interface FormField {
  [key: string]: any
}

export interface FormManagerConfig<T extends FormField> {
  initialValues: T
  onAddThunk: (payload: any) => any
  onUpdateThunk: (id: string, payload: any) => any
  onFetchThunk: () => any
  onSuccess?: (isEditing: boolean) => void
  onError?: (error: string) => void
  transformPayload?: (values: T, isEditing: boolean) => any
}

interface UseFormManagerReturn<T extends FormField> {
  values: T
  isEditing: boolean
  editingId: string | null
  isSubmitting: boolean
  setFieldValue: (key: keyof T, value: any) => void
  setFieldValues: (newValues: Partial<T>) => void
  handleEdit: (item: any, dateFields?: string[]) => void
  handleReset: () => void
  handleSubmit: (onSuccess: (isEditing: boolean) => void, onError: (msg: string) => void) => (e?: React.FormEvent) => Promise<void>
  getFieldValue: (key: keyof T) => any
  SubmittingOverlay: () => JSX.Element | null
}

/**
 * Hook para manejar formularios completos con Redux
 * Soporta edición, creación, reset y sincronización con store
 * Reduce significativamente el boilerplate en managers
 */
export const useFormManager = <T extends FormField>(
  config: FormManagerConfig<T>
): UseFormManagerReturn<T> => {
  const { initialValues, onAddThunk, onUpdateThunk, onFetchThunk, transformPayload } = config
  const dispatch = useDispatch<AppDispatch>()

  const [values, setValues] = useState<T>(initialValues)
  const [isEditing, setIsEditing] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const setFieldValue = useCallback((key: keyof T, value: any) => {
    setValues(prev => ({ ...prev, [key]: value }))
  }, [])

  const setFieldValues = useCallback((newValues: Partial<T>) => {
    setValues(prev => ({ ...prev, ...newValues }))
  }, [])

  const handleReset = useCallback(() => {
    setValues(initialValues)
    setIsEditing(false)
    setEditingId(null)
  }, [initialValues])

  const handleEdit = useCallback(
    (item: any, dateFields: string[] = []) => {
      setEditingId(item.id)
      setIsEditing(true)

      // Preparar valores incluyendo parsing especial para fechas
      const itemValues: any = { ...item }
      dateFields.forEach(field => {
        if (itemValues[field]) {
          itemValues[field] = parseDateToDayjs(itemValues[field])
        }
      })

      setValues(itemValues as T)
      window.scrollTo({ top: 600, behavior: 'smooth' })
    },
    []
  )

  const handleSubmit = useCallback(
    (onSuccess: (isEditing: boolean) => void, onError: (msg: string) => void) =>
      async (e?: React.FormEvent) => {
        e?.preventDefault()
        ;
        setIsSubmitting(true)
        try {
          const isEdit = !!editingId
          const payload = transformPayload ? transformPayload(values, isEdit) : values

       if (isEdit && editingId) {
            await dispatch(onUpdateThunk(editingId, payload)).unwrap()
            onSuccess(true)
          } else {
            await dispatch(onAddThunk(payload)).unwrap()
            onSuccess(false)
          }
          await dispatch(onFetchThunk())
          handleReset()          
        } catch (error: any) {
          console.error('Error in handleSubmit:', error)
          onError(error?.message || error || 'Error en la operación')
        } finally {
          setIsSubmitting(false)
        }
      },
    [editingId, values, dispatch, onAddThunk, onUpdateThunk, onFetchThunk, handleReset, transformPayload]
  )

  const getFieldValue = useCallback(
    (key: keyof T) => values[key],
    [values]
  )

  return {
    values,
    isEditing,
    editingId,
    isSubmitting,
    setFieldValue,
    setFieldValues,
    handleEdit,
    handleReset,
    handleSubmit,
    getFieldValue,
    SubmittingOverlay: useCallback(() => {
      if (!isSubmitting) return null
      return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.35)' }}>
          <Box sx={{ p: 3, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', gap: 2, boxShadow: 3 }}>
            <CircularProgress size={28} />
            <Typography variant="body1">Procesando, por favor espere...</Typography>
          </Box>
        </Box>
      )
    }, [isSubmitting]),
  }
}
